import { Suspense, lazy, useState } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import { DashboardLayout } from '../layouts/dashboard';
import { ApplyLayout } from '../layouts/applyLayout';
import DocsLayout from '../layouts/docsLayout';

// components
import Section from '../components/Section'; // used for updating cognito config

// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import VerifyCode from '../pages/auth/VerifyCode';
import RoleBasedGuard from '../guards/RoleBasedGuard';

import { ROLES } from '../utils/enums'
import TermsAndConditions from '../pages/auth/TermsAndConditions';

// ----------------------------------------------------------------------

/* 
General User
No password

MD (Active & Visiting)
Patient Record
Schedules of patient
Check their schedule
Log-in bin for patient
Able to schedule their patients

Clinic Manager
Input inventory in and out
Order reminders to the inventory manager
Sales 
Per session
Per package
Succeeding package use
Products
Sales (For MD use)
Can generate billing for MDS
Can generate billing for patients of Regenesis
Facility fee
Machine Fee
Bill for patients of MDS 
Census of therapist
Consumable needs
How much check has contributed
Generate Sales Report

Patient
Reminders
Next TX
Products
Delivery of products (Automatic monthly (?))
Online payment
Book Schedules
Membership

Inventory Manager
Inventory in and out to different clinic
Reminder for orders
Census of suppliers and their division of products
Trackers of how much is spent in different suppliers
*/

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense /* fallback={<LoadingScreen isDashboard={pathname.includes('/admin')} />} */>
      <Component {...props} />
    </Suspense>
  );
};

const AuthRoute = (section, register) => {
  const registerPath = register ? `/${section}/auth/register` : '';

  const route = {
    path: section,
    children: [
      {
        path: 'auth',
        children: [
          { path: 'terms-and-conditions', element: <TermsAndConditions noAgreeButton /> },
          {
            path: 'login',
            element: (
              <Section section={section}>
                <GuestGuard rootPath={`/${section}`}>
                  <Login resetPasswordPath={`/${section}/auth/reset-password`} registerPath={registerPath} />
                </GuestGuard>
              </Section>
            ),
          },
          {
            path: 'reset-password',
            element: (
              <Section section={section}>
                <GuestGuard rootPath={`/${section}`}>
                  <ResetPassword
                    newPasswordPath={`/${section}/auth/new-password`}
                    loginPath={`/${section}/auth/login`}
                  />
                </GuestGuard>
              </Section>
            ),
          },
          {
            path: 'new-password',
            element: (
              <Section section={section}>
                <NewPassword rootPath={`/${section}`} />
              </Section>
            ),
          },
          {
            path: 'verify',
            element: (
              <Section section={section}>
                <VerifyCode />
              </Section>
            ),
          },
        ],
      },
    ],
  };

  if (register)
    route.children[0].children.push({
      path: 'register',
      element: (
        <Section section={section}>
          <GuestGuard rootPath={`/${section}`}>
            <Register loginPath={`/${section}/auth/login`} />
          </GuestGuard>
        </Section>
      ),
    });

  return route;
};

export default function Router() {

  const deptRoles = []

  return useRoutes([
    {
      path: '',
      element: (
        <GuestGuard rootPath={`/`}>
          <GuestLandingPage />
        </GuestGuard>
      ),
    },
    {
      path: 'patient',
      element: (
        <>
          <Section section="patient">
            <AuthGuard resetPasswordPath="/patient/auth/reset-password" registerPath="/patient/auth/register">
              <DashboardLayout />
            </AuthGuard>
          </Section>
        </>
      ),
      children: [
        { path: '', element: <Navigate to="/patient/home" replace /> },
        { path: 'home', element: <Home /> },
        { path: 'my-appointments', element: <MyAppointements /> },
        { path: 'my-orders', element: <MyOrders /> },
        { path: 'appointments', element: < PatientAppointmentsView /> },

        { path: 'shop', element: <Shop isPatient /> },
        { path: 'shop/cart', element: <ProductCart /> },
        { path: 'products/:id/view', element: <ProductDetails /> },          
      ],
    },
    AuthRoute('patient', true),

    {
      path: 'employee',
      element: (
        <Section section="employee">
          <AuthGuard resetPasswordPath="/employee/auth/reset-password">
            <DashboardLayout />
          </AuthGuard>
        </Section>
      ),
      children: [
        ...[
          { path: '', element: <Navigate to="home" replace />, roles: ROLES.employee },
          { path: 'home', element: <StaffDashboard />, roles: ROLES.employee },

          { path: 'manager-dash', element: <ManagerDash />, roles: ROLES.admin },

          { path: 'patients/medical-records', element: <MedicalRecordsList />, roles: ROLES.employee },
          { path: 'patients/medical-records/:id/create', element: <MedicalRecordCreateEdit />, roles: ROLES.employee },
          { path: 'patients/medical-records/:id/edit', element: <MedicalRecordCreateEdit />, roles: ROLES.employee },
          { path: 'patients/medical-records/:id/list', element: <PatientMedicalRecordsList />, roles: ROLES.employee },
          { path: 'patients/medical-records/:id/edit/:medRecId', element: <MedicalRecordCreateEdit />, roles: ROLES.employee },

          { path: 'patients', element: <Navigate to="list" />, roles: ROLES.employee },
          { path: 'patients/list', element: <PatientList />, roles: ROLES.employee },
          { path: 'patients/create', element: <PatientCreateEdit />, roles: ROLES.employee },
          { path: 'patients/:id/edit', element: <PatientCreateEdit />, roles: ROLES.employee },

          { path: 'doctors', element: <Navigate to="list" />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'doctors/list', element: <DoctorList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'doctors/create', element: <DoctorCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'doctors/:id/edit', element: <DoctorCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'therapists', element: <Navigate to="list" />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'therapists/list', element: <TherapistList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'therapists/create', element: <TherapistCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'therapists/:id/edit', element: <TherapistCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'employees', element: <Navigate to="list" />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/list', element: <EmployeeList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/create', element: <EmployeeCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/:id/edit', element: <EmployeeCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'employees/:id/salary-structure', element: <SalaryStructureEmployee />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'employees/salary-templates/list', element: <SalaryTemplatesList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/salary-templates/create', element: <SalaryTemplateCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/salary-templates/:id/edit', element: <SalaryTemplateCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'employees/payroll-entries/list', element: <SalaryPayrollEntryList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/payroll-entries/create', element: <SalaryPayrollEntryCreateEdit />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'employees/payroll-entries/:id/edit', element: <SalaryPayrollEntryCreateEdit isEdit />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'attendance', element: <Navigate to="records" />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/records', element: <AttendanceList isLeave />, roles: ROLES.employee },
          { path: 'attendance/records/doctor/:employeeId', element: <TimeAttendance isDoctor />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/records/employee/:employeeId', element: <TimeAttendance isEmployee />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/records/therapist/:employeeId', element: <TimeAttendance isTherapist />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/edit-requests', element: <AttendanceEditRequestList />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/leave-request', element: <LeaveRequestList isLeave />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'attendance/overtime-request', element: <OvertimeList />, roles: [...ROLES.clinicManager, 'hr' ] },
          
          { path: 'my-attendance/records', element: <TimeAttendance isSelf />, roles: ROLES.employee },
          { path: 'my-attendance/edit-requests', element: <AttendanceEditRequestList isSelf />, roles: ROLES.employee },
          { path: 'my-attendance/leave-request', element: <LeaveRequestList isSelf />, roles: ROLES.employee },
          { path: 'my-attendance/overtime-request', element: <OvertimeList isSelf />, roles: ROLES.employee },

          { path: 'my-commissions', element: <MyCommissions />, roles: ROLES.employee },

          { path: 'products', element: <Navigate to="list" />, roles: ROLES.employee },
          { path: 'products/list', element: <ProductList />, roles: ROLES.employee },
          { path: 'products/create', element: <ProductCreateEdit />, roles: ROLES.employee },
          { path: 'products/:id/edit', element: <ProductCreateEdit isEdit />, roles: ROLES.employee },
          { path: 'products/:id/view', element: <ProductDetails />, roles: ROLES.employee },

          { path: 'inventory', element: <InventoryDashboard />, roles: ROLES.employee },
          { path: 'inventory/list', element: <InventoryList />, roles: ROLES.employee },
          { path: 'inventory/create', element: <ProductCreateEdit />, roles: ROLES.employee },
          // /inventory/${id}${branch ? `branch=?${branch}` : ''}
          { path: 'inventory/:id', element: <ProductCreateEdit isEdit />, roles: ROLES.employee },

          { path: 'shop', element: <Shop />, roles: ROLES.employee },
          { path: 'shop/cart', element: <ProductCart isManager />, roles: ROLES.employee },

          { path: 'appointments', element: <Navigate to="list" />, roles: ROLES.employee },
          { path: 'appointments/list', element: <StaffDashboard isAppointmentList />, roles: ROLES.employee },
          { path: 'appointments/create', element: <AppointmentCreateEdit />, roles: ROLES.employee },
          { path: 'appointments/:id/edit', element: <AppointmentCreateEdit />, roles: ROLES.employee },

          { path: 'booking-blocks', element: <BookingBlocksPage />, roles: [...ROLES.admin, ...ROLES.clinicManager] },

          { path: 'transactions', element: <Navigate to="list" />, roles: ROLES.employee },
          { path: 'transactions/list', element: <TransactionList />, roles: ROLES.employee },
          { path: 'transactions/create', element: <TransactionCreate />, roles: ROLES.employee },
          { path: 'transactions/online', element: <TransOnlineOrderList />, roles: ROLES.employee },
          { path: 'transactions/commissions', element: <TransactionCommissionList />, roles: ROLES.clinicManager },

          { path: 'reports/sales', element: <Reports />, roles: ROLES.clinicManager },
          { path: 'reports/commissions', element: <CommissionManagementReport />, roles: ROLES.clinicManager },
          { path: 'reports/form-1', element: <MonthOneEmployeeEvalForm />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'reports/form-2', element: <MonthTwoEmployeeEvalForm />, roles: [...ROLES.clinicManager, 'hr' ] },
          { path: 'reports/form-3', element: <ManagementPerfEvalForm />, roles: ROLES.clinicManager },
          { path: 'reports/evaluations', element: <EvaluationList />, roles: [...ROLES.clinicManager, 'hr' ] },

          { path: 'cash-funds/', element: <PettyCashFundList />, roles: ROLES.clinicManager },
          { path: 'cash-requests/', element: <PettyCashRequestList />, roles: ROLES.clinicManager },
          { path: 'my-cash-requests/', element: <PettyCashRequestList isSelf />, roles: ROLES.employee },

          { path: 'settings/', element: <Settings />, roles: ROLES.clinicManager },
          { path: 'settings/:id/edit', element: <SettingsCreateEdit />, roles: ROLES.clinicManager },

        ].map(v => {
          const { element, roles, ...rest } = v
          return ({ ...rest, element: <RoleBasedGuard roles={roles ?? ROLES.clinicManager} hasContent >{element}</RoleBasedGuard> })
        }),

      ],
    },
    AuthRoute('employee'),
    // Admin Docs
    {
      path: 'admin/docs',
      element:
        <Section section="admin">
          <AuthGuard resetPasswordPath="/admin/auth/reset-password">
            <DocsLayout section="admin" />
          </AuthGuard>
        </Section>,
      children: [
        { path: '', element: <Navigate to="/admin/docs/main" /> },
        { path: 'main', element: <NewPage /> },

        // refer to src/layouts/docsLayout/navbar/NavConfig.js for the structure of the doc routes
        {
          path: 'menu_level_1',
          children: [
            { path: '', element: <Navigate to="/admin/docs/main" /> },
            { path: 'menu_level_2a', element: <NewPage content="This is the content for route menu_level_1/menu_level_2a" /> },
            {
              path: 'menu_level_2b',
              children: [
                { path: 'menu_level_3a', element: <NewPage content="This is the content for route menu_level_1/menu_level_2b/menu_level_3a" /> },
                {
                  path: 'menu_level_3b',
                  children: [
                    { path: 'menu_level_4a', element: <NewPage content="This is the content for route menu_level_1/menu_level_2b/menu_level_3b/menu_level_4a" /> },
                    { path: 'menu_level_4b', element: <NewPage content="This is the content for route menu_level_1/menu_level_2b/menu_level_3b/menu_level_4b" /> },
                  ]
                },
              ]
            },
          ],
        }
      ]
    }, 
    {
      path: '*',
      element: <NotFound />,
    },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));

// Docs
const NewPage = Loadable(lazy(() => import('../pages/docs/NewPage')));

// GUEST
const GuestLandingPage = Loadable(lazy(() => import('../pages/guest/guest-landing-page')));

// MANAGER
const ManagerDash = Loadable(lazy(() => import('../pages/ManagerDashboard')));

const MedicalRecordsList = Loadable(lazy(() => import('../pages/manager/MedicalRecordsList')));
const MedicalRecordCreateEdit = Loadable(lazy(() => import('../pages/manager/MedicalRecordCreateEdit')));
const PatientMedicalRecordsList = Loadable(lazy(() => import('../pages/manager/PatientMedicalRecordList')));

const PatientList = Loadable(lazy(() => import('../pages/manager/PatientList')));
const PatientCreateEdit = Loadable(lazy(() => import('../pages/manager/PatientCreateEdit')));

const DoctorList = Loadable(lazy(() => import('../pages/manager/DoctorList')));
const DoctorCreateEdit = Loadable(lazy(() => import('../pages/manager/DoctorCreateEdit')));

const TherapistList = Loadable(lazy(() => import('../pages/manager/TherapistList')));
const TherapistCreateEdit = Loadable(lazy(() => import('../pages/manager/TherapistCreateEdit')));

const EmployeeList = Loadable(lazy(() => import('../pages/manager/EmployeeList')));
const EmployeeCreateEdit = Loadable(lazy(() => import('../pages/manager/EmployeeCreateEdit')));

const SalaryTemplatesList = Loadable(lazy(() => import('../pages/manager/SalaryTemplates')));
const SalaryTemplateCreateEdit = Loadable(lazy(() => import('../pages/manager/SalaryTemplateCreateEdit')));

const SalaryStructureEmployee = Loadable(lazy(() => import('../pages/manager/SalaryStructureEmployee')));

const SalaryPayrollEntryList = Loadable(lazy(() => import('../pages/manager/SalaryPayrollEntryList')));
const SalaryPayrollEntryCreateEdit = Loadable(lazy(() => import('../pages/manager/SalaryPayrollEntryCreateEdit')));

const AttendanceList = Loadable(lazy(() => import('../pages/AttendanceRecords')));
const TimeAttendance = Loadable(lazy(() => import('../pages/TimeAttendancePage')));
const AttendanceEditRequestList = Loadable(lazy(() => import('../pages/AttendanceEditRequestList')));
const LeaveRequestList = Loadable(lazy(() => import('../pages/LeaveRequestList')));
const OvertimeList = Loadable(lazy(() => import('../pages/OvertimeList')));
const MyCommissions = Loadable(lazy(() => import('../pages/MyCommissions')));

const ProductList = Loadable(lazy(() => import('../pages/manager/ProductList')));
const ProductCreateEdit = Loadable(lazy(() => import('../pages/manager/ProductCreateEdit')));
const ProductDetails = Loadable(lazy(() => import('../pages/manager/ProductDetails')));
const ProductCart = Loadable(lazy(() => import('../pages/EcommerceCheckout')));

const Shop = Loadable(lazy(() => import('../pages/ShopPage')));

const TransactionCreate = Loadable(lazy(() => import('../pages/manager/TransactionCreate')));
const TransactionList = Loadable(lazy(() => import('../pages/manager/TransactionList')));
const TransOnlineOrderList = Loadable(lazy(() => import('../pages/manager/TransOnlineOrderList')));
const TransactionCommissionList = Loadable(lazy(() => import('../pages/manager/TransactionCommissionList')));

const Reports = Loadable(lazy(() => import('../pages/manager/Reports')));
const CommissionManagementReport = Loadable(lazy(() => import('../pages/manager/CommissionManagementReport')));
const MonthOneEmployeeEvalForm = Loadable(lazy(() => import('../pages/monthOneEmployeeEvalForm')));
const MonthTwoEmployeeEvalForm = Loadable(lazy(() => import('../pages/monthTwoEmployeeEvalForm')));
const ManagementPerfEvalForm = Loadable(lazy(() => import('../pages/managementPerfEvalForm')));
const EvaluationList = Loadable(lazy(() => import('../pages/EvaluationList')));

const PettyCashFundList = Loadable(lazy(() => import('../pages/PettyCashFundList')));
const PettyCashRequestList = Loadable(lazy(() => import('../pages/PettyCashRequestList')));

const Settings = Loadable(lazy(() => import('../pages/manager/SettingsList')));
const SettingsCreateEdit = Loadable(lazy(() => import('../pages/manager/SettingsCreateEdit')));

// Staff/Employees
const StaffDashboard = Loadable(lazy(() => import('../pages/StaffDashboard')));

// Appointments
const AppointmentCreateEdit = Loadable(lazy(() => import('../pages/manager/AppointmentCreateEdit')));
const BookingBlocksPage = Loadable(lazy(() => import('../pages/employee/BookingBlocksPage')));

// PATIENT 
const Home = Loadable(lazy(() => import('../pages/PatientHome')));
const PatientAppointmentsView = Loadable(lazy(() => import('../pages/patient/PatientAppointmentsViewPage')));
const MyAppointements = Loadable(lazy(() => import('../pages/patient/MyAppointments')));
const MyOrders = Loadable(lazy(() => import('../pages/patient/MyOrders')));
// INVENTORY
const InventoryDashboard = Loadable(lazy(() => import('../pages/inventory/InventoryDashboard')));
const InventoryList = Loadable(lazy(() => import('../pages/inventory/InventoryList')));

// GENERAL
const NotFound = Loadable(lazy(() => import('../pages/Page404')));